import { SearchOutlined } from "@ant-design/icons";
import {
  Button,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  message,
} from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { serverUrl } from "../../../../../nestserver";
import { GRADE_OPTIONS } from "../../../../../helper/assessmentOptions";

const GenerateCard = () => {
  const { Option } = Select;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [classId, setClassId] = useState();
  const searchInputRef = useRef(null);
  const [progressIds, setProgressIds] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  // One template for the whole class — the per-row Select below still allows an
  // override, but picking it once covers the common case instead of 40 times.
  const [classTemplateId, setClassTemplateId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);


  //handle class Select change
  const handleSelectChange = (classId) => {
    setClassId(classId);
  };
  const handleTemplateChange = (templateId, studentId) => {
    setSelectedTemplates((prev) => {
      const existingIndex = prev.findIndex((t) => t.studentId === studentId);
      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex] = { studentId, templateId };
        return updated;
      }
      return [...prev, { studentId, templateId }];
    });
  };

  // fetch student
  async function fetchStudent(classId) {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/progress-card-v2/?class=${classId}`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }

  const getStudent = useQuery(
    ["progress-card", classId],
    () => fetchStudent(classId),
    {
      enabled: !!classId,
    },
  );
  useEffect(() => {
    // Seed each row from the card it already carries — but only while no
    // class-level template is chosen. Once one is, that selection drives every
    // row and seeding here would silently override it.
    if (classTemplateId) return;
    if (getStudent?.isSuccess && getStudent?.data?.result) {
      const initialTemplates = getStudent.data.result
        .filter((student) => student?.progress_card)
        .map((student) => ({
          studentId: student._id,
          templateId:
            student?.progress_card?.template?._id ||
            student?.progress_card?.template,
        }));
      setSelectedTemplates(initialTemplates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getStudent?.data, getStudent?.isSuccess, classTemplateId]);

  // Picking a template for the class clears the per-row overrides, so the top
  // selector genuinely applies to every row. A row can still be changed
  // individually afterwards.
  useEffect(() => {
    if (classTemplateId) setSelectedTemplates([]);
  }, [classTemplateId]);

  //fetch template
  async function fetchTemplate(classId) {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/progress-card-template-v2/?class=${classId}`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }

  const getTemplate = useQuery(
    ["progress-card-template", classId],
    () => fetchTemplate(classId),
    {
      enabled: !!classId,
    },
  );

  async function fetchClasses() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/classes/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

    return res.data;
  }
  const classes = useQuery("classes", fetchClasses);
  const handleNotify = () => {
    const payload = {
      progress_card_ids: progressIds,
    };
    if (progressIds && progressIds?.length > 0) {
      axios
        .patch(serverUrl + "/progress-card-v2/notify", payload, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
        .then((res) => {
          queryClient.refetchQueries(["progress-card", classId]);
          message.success(res?.data?.result?.message);
          // The cards are notified now and their rows lock, so leave nothing
          // ticked.
          setSelectedRowKeys([]);
          setProgressIds([]);
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            message.error(err?.response?.data?.message);
          }
        });
    }
  };

  const [, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputRef}
          placeholder={`Search Subject`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={
              <SearchOutlined
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            }
            size="small"
            style={{ width: 90 }}
            className="inline-flex items-center justify-center"
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInputRef.current?.select(), 100);
      }
    },
    render: (text) => (searchedColumn === dataIndex ? text : text),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };
  // The template in force for a row: its own override if set, otherwise the
  // one chosen for the whole class.
  const templateIdFor = (record) =>
    selectedTemplates.find((template) => template.studentId === record._id)
      ?.templateId || classTemplateId;

  // Which progress card applies to a row: the one for the template in force,
  // falling back to whatever card the row already carries. Shared by the Status
  // column, the Action column and the checkbox so the three can never disagree
  // about whether a card exists.
  const cardFor = (record) => {
    const templateId = templateIdFor(record);
    return templateId
      ? record?.progress_cards?.find(
          (data) => data?.template?._id === templateId,
        )
      : record?.progress_card;
  };

  // Only a card sitting at `ready` can be notified. Already-notified and
  // acknowledged cards are excluded too — the server ignores them on the
  // status filter, so re-sending them changes nothing but does reach parents.
  // The checkbox feeds two actions now — Mark Ready acts on drafts, Notify on
  // ready cards — so it has to allow both. Each button filters the selection
  // to the rows it can actually act on.
  const isCardSelectable = (record) => {
    const card = cardFor(record);
    return (
      !!card?._id && (card?.status === "draft" || card?.status === "ready")
    );
  };

  const serverReasonOf = (error) => {
    const reason = error?.response?.data?.message;
    return Array.isArray(reason) ? reason[0] : reason;
  };

  const rows = getStudent?.isSuccess ? getStudent?.data?.result || [] : [];

  // Readiness at a glance, derived from rows already loaded — no extra request.
  const tally = rows.reduce(
    (acc, row) => {
      const status = cardFor(row)?.status;
      if (status === "ready") acc.ready += 1;
      else if (status === "draft") acc.draft += 1;
      else if (status === "notified" || status === "acknowledged")
        acc.notified += 1;
      else acc.pending += 1;
      return acc;
    },
    { ready: 0, draft: 0, notified: 0, pending: 0 },
  );

  // Overall grade and remark are edited inline so a whole class can be filled
  // without opening 40 screens. Saved per row on change/blur rather than
  // batched, so nothing is lost if the page is left mid-edit.
  const [overallEdits, setOverallEdits] = useState({});
  const [savingRowId, setSavingRowId] = useState(null);

  const overallValue = (card, field) =>
    overallEdits[card?._id]?.[field] ?? card?.[field] ?? "";

  const saveOverall = async (card, patch) => {
    if (!card?._id) return;
    setSavingRowId(card._id);
    try {
      // PUT recomputes total_marks from the stored subjects when none are
      // sent, so a partial body leaves the marks untouched.
      await axios.put(
        `${serverUrl}/progress-card-v2/${card._id}`,
        patch,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      queryClient.refetchQueries(["progress-card", classId]);
    } catch (e) {
      message.error(
        serverReasonOf(e) || "Could not save. Please try again.",
      );
    } finally {
      setSavingRowId(null);
    }
  };

  const handleGenerateAll = async () => {
    if (!classTemplateId) {
      message.warning("Choose a template first");
      return;
    }
    setBulkBusy(true);
    try {
      const res = await axios.post(
        serverUrl + "/progress-card-v2/generate-all",
        { templateId: classTemplateId },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      message.success(res?.data?.result?.message || "Cards generated");
      queryClient.refetchQueries(["progress-card", classId]);
    } catch (e) {
      message.error(serverReasonOf(e) || "Could not generate. Please try again.");
    } finally {
      setBulkBusy(false);
    }
  };

  // Only drafts can move to ready, so send just those from the selection.
  const readyIdsInSelection = rows
    .filter((row) => selectedRowKeys?.includes(row?._id))
    .map((row) => cardFor(row))
    .filter((card) => card?._id && card?.status === "ready")
    .map((card) => card._id);

  const draftIdsInSelection = rows
    .filter((row) => selectedRowKeys?.includes(row?._id))
    .map((row) => cardFor(row))
    .filter((card) => card?._id && card?.status === "draft")
    .map((card) => card._id);

  const handleMarkReady = async () => {
    if (draftIdsInSelection.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await axios.patch(
        serverUrl + "/progress-card-v2/mark-ready",
        { progress_card_ids: draftIdsInSelection },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      const result = res?.data?.result;
      // The server refuses cards with no overall grade or remark and names
      // them, so the teacher knows exactly which rows still need filling.
      if (result?.success === false) {
        message.warning(result?.message || "Nothing could be marked ready");
      } else {
        message.success(result?.message || "Marked ready");
      }
      queryClient.refetchQueries(["progress-card", classId]);
      // Whatever was ticked has been dealt with; start the next pick clean.
      setSelectedRowKeys([]);
    } catch (e) {
      message.error(serverReasonOf(e) || "Could not update. Please try again.");
    } finally {
      setBulkBusy(false);
    }
  };

  const columns = [
    {
      title: "Student Id",
      width: 120,
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record}
          style={{ color: "#111111" }}
          className="text-base text-center"
        >
          {record?.unique_id}
        </div>
      ),
    },
    {
      title: "Name",
      width: 180,
      key: "name",
      align: "center",
      ...getColumnSearchProps(["first_name", "last_name"]),
      render: (record, index) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center capitalize"
        >
          {record?.first_name + " " + record?.last_name}
        </div>
      ),
      onFilter: (value, record) =>
        (
          record?.first_name?.toLowerCase() +
          " " +
          record?.last_name?.toLowerCase()
        ).includes(value.toLowerCase()),
    },
    {
      title: "Template",
      width: 180,
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div style={{ color: "#111111" }} className="text-base text-center">
          <Select
            showSearch
            showArrow
            placeholder="Select template"
            className="w-full bg-gray-300 capitalize"
            style={{
              border: "6px",
              backgroundColor: "#EBEBEB",
            }}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            filterSort={(optionA, optionB) =>
              optionA.children
                .toLowerCase()
                .localeCompare(optionB.children.toLowerCase())
            }
            getPopupContainer={(trigger) => trigger.parentNode}
            onChange={(value) => {
              handleTemplateChange(value, record?._id);
            }}
            // Same resolver the Status, Marks and Overall columns use, so what
            // is displayed can never disagree with the card being acted on.
            // This previously preferred the row's last card over the
            // class-level pick, so choosing Term 1 at the top still showed
            // Term 2 in the rows.
            value={templateIdFor(record) || undefined}
          >
            {getTemplate?.isSuccess &&
              getTemplate?.data?.result?.map((data) => {
                // A template already notified for this student is dropped from
                // the list, so it cannot be re-issued.
                const matchingTemplate = record?.progress_cards?.find(
                  (template) =>
                    (template?.template?._id === data?._id ||
                      template?.template === data?._id) &&
                    template?.status === "notified",
                );
                // ...except the one currently in force, which must always have
                // an Option or antd has nothing to render and falls back to
                // printing the raw id. This used to compare against the row's
                // last card instead, which is a different template whenever a
                // student has more than one.
                const inForce = templateIdFor(record) === data?._id;
                return !matchingTemplate || inForce ? (
                  <Option
                    key={data?._id}
                    value={data?._id}
                    className="capitalize"
                  >
                    {data?.name}
                  </Option>
                ) : null;
              })}
          </Select>
        </div>
      ),
    },
    {
      title: "Status",
      width: 110,
      dataIndex: [],
      key: "status",
      align: "center",
      render: (record, _, index) => {
        const recData = cardFor(record);

        return (
          <div
            key={index}
            style={{ color: "#111111" }}
            className="text-base text-center capitalize"
          >
            {recData ? recData?.status : "unready"}
          </div>
        );
      },
    },
    {
      // Pulled from the exams at generate time, so it is read-only here.
      title: "Total Marks",
      dataIndex: [],
      key: "total_marks",
      align: "center",
      width: 110,
      render: (record) => {
        const card = cardFor(record);
        if (card?.total_marks == null) {
          return <div className="text-base text-center">--</div>;
        }
        return (
          <div className="text-base text-center">
            {card.total_marks}
            {card.max_marks ? (
              <span className="text-gray-500"> / {card.max_marks}</span>
            ) : null}
          </div>
        );
      },
    },
    {
      // Worked out by the server from the published cards of this template.
      title: "Rank",
      dataIndex: [],
      key: "rank",
      align: "center",
      width: 90,
      render: (record) => {
        const card = cardFor(record);
        return (
          <div className="text-base text-center">
            {card?.rank ? `${card.rank} / ${card.out_of}` : "--"}
          </div>
        );
      },
    },
    {
      // The one part of a card no bulk step can derive, so it is captured here
      // rather than behind a per-student navigation.
      title: "Overall Grade",
      dataIndex: [],
      key: "overall_grade",
      align: "center",
      width: 130,
      render: (record) => {
        const card = cardFor(record);
        // No card yet means no id to save against, so these stay locked
        // until Generate All (or the row's Generate) has run.
        const lockReason = !card?._id
          ? "Generate this report card first"
          : card?.status === "notified" || card?.status === "acknowledged"
            ? "Already shared with the family"
            : null;
        const locked = !!lockReason;
        const control = (
          <Select
            value={overallValue(card, "overall_grade") || undefined}
            placeholder="Grade"
            style={{ width: "100%" }}
            allowClear
            disabled={locked || savingRowId === card?._id}
            getPopupContainer={(trigger) => trigger.parentNode}
            options={GRADE_OPTIONS.map((g) => ({ label: g, value: g }))}
            onChange={(value) => {
              setOverallEdits((prev) => ({
                ...prev,
                [card._id]: { ...prev[card._id], overall_grade: value ?? "" },
              }));
              saveOverall(card, { overall_grade: value ?? "" });
            }}
          />
        );
        // A disabled control emits no mouse events, so the tooltip has to sit
        // on a wrapper rather than on the control itself.
        return locked ? (
          <Tooltip title={lockReason}>
            <span style={{ display: "block" }}>{control}</span>
          </Tooltip>
        ) : (
          control
        );
      },
    },
    {
      title: "Overall Remark",
      dataIndex: [],
      key: "overall_comment",
      align: "center",
      width: 260,
      render: (record) => {
        const card = cardFor(record);
        const lockReason = !card?._id
          ? "Generate this report card first"
          : card?.status === "notified" || card?.status === "acknowledged"
            ? "Already shared with the family"
            : null;
        const locked = !!lockReason;
        const control = (
          <Input.TextArea
            value={overallValue(card, "overall_comment")}
            placeholder="Overall remark"
            style={{ minWidth: 200 }}
            autoSize={{ minRows: 2, maxRows: 4 }}
            disabled={locked || savingRowId === card?._id}
            onChange={(e) =>
              setOverallEdits((prev) => ({
                ...prev,
                [card._id]: {
                  ...prev[card._id],
                  overall_comment: e.target.value,
                },
              }))
            }
            // Saved on blur, not per keystroke.
            onBlur={() => {
              const edited = overallEdits[card?._id]?.overall_comment;
              if (edited !== undefined && edited !== card?.overall_comment) {
                saveOverall(card, { overall_comment: edited });
              }
            }}
          />
        );
        return locked ? (
          <Tooltip title={lockReason}>
            <span style={{ display: "block" }}>{control}</span>
          </Tooltip>
        ) : (
          control
        );
      },
    },
    {
      title: "Applied Template",
      width: 150,
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record}
          style={{ color: "#111111" }}
          className="text-base text-center capitalize cursor-pointer"
          onClick={() => {
            setSelectedRecord(record?.progress_cards);
            setIsModalVisible(true);
          }}
        >
          {record?.progress_cards?.length}
        </div>
      ),
    },
    {
      title: "Last Report",
      width: 150,
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record}
          style={{ color: "#111111" }}
          className="text-base text-center capitalize"
        >
          {record?.progress_card?.status === "notified" &&
          record?.progress_card?.notified_at
            ? moment(record?.progress_card?.notified_at).format(
                "DD MMMM YYYY,hh:mm A",
              )
            : "--"}
        </div>
      ),
    },

    {
      title: "Action",
      width: 120,
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => {
        const recData = cardFor(record);

        const isUnready = !recData || recData?.status === "unready";

        return (
          <div
            className="text-base font-semibold text-center text-blue-500 cursor-pointer"
            key={record}
          >
            {isUnready ? (
              <Button
                onClick={() => {
                  const templateId = templateIdFor(record);
                  if (!templateId) {
                    message.warning("Please select a template first");
                    return;
                  }
                  navigate(
                    `/dashboard/admin/menu/student/${record?._id}/template/${templateId}`,
                  );
                }}
              >
                Generate
              </Button>
            ) : (
              <Button
                onClick={() =>
                  navigate(
                    `/dashboard/admin/menu/student/${record?._id}/template/${
                      templateIdFor(record) || recData?.template?._id
                    }/report/${recData?._id}`,
                  )
                }
              >
                View
              </Button>
            )}
          </div>
        );
      },
    },
  ];
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys, selectedRows) => {
      setSelectedRowKeys(selectedKeys);
      selectedRows &&
        selectedRows?.length > 0 &&
        setProgressIds(
          selectedRows
            ?.map((item) => item?.progress_card?._id)
            .filter((id) => id !== null && id !== undefined), // Filter out null and undefined values
        );
    },
    // Only a generated card can be notified, so block selecting the rest.
    getCheckboxProps: (record) => {
      const card = cardFor(record);
      const selectable = isCardSelectable(record);
      const alreadySent =
        card?.status === "notified" || card?.status === "acknowledged";
      return {
        disabled: !selectable,
        title: selectable
          ? undefined
          : alreadySent
            ? "Already shared with this student's family"
            : "Generate this report card first",
      };
    },
  };

  // Notify only ever sends cards sitting at `ready`. Derived through cardFor
  // like the draft list, so both honour the class-level template rather than
  // whatever card the row happens to carry.
  useEffect(() => {
    setProgressIds(readyIdsInSelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getStudent?.data, selectedRowKeys, classTemplateId, selectedTemplates]);

  return (
    <>
      <div className="mt-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className=" bg-white drop-shadow-md p-8 w-full "
        >
          <div className="text-2xl text-orange-500 font-semibold pb-8">
            Generate Report Card
          </div>
          <div className="flex gap-4 w-310 items-center">
            <h3 className="text-base font-semibold">Class</h3>
            <Select
              showSearch
              showArrow
              style={{ width: 200 }}
              placeholder="Select Class"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              filterSort={(optionA, optionB) =>
                optionA.children
                  .toLowerCase()
                  .localeCompare(optionB.children.toLowerCase())
              }
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={(value) => handleSelectChange(value)}
            >
              {classes?.isSuccess &&
                classes?.data?.map((data) => {
                  return (
                    <Option key={data._id} value={data._id}>
                      {data.name}
                    </Option>
                  );
                })}
            </Select>

            {/* Chosen once for the class; the per-row Select still overrides. */}
            <h3 className="text-base font-semibold pl-4">Template</h3>
            <Select
              showSearch
              showArrow
              style={{ width: 220 }}
              placeholder="Select Template"
              value={classTemplateId || undefined}
              disabled={!classId}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={(value) => setClassTemplateId(value)}
            >
              {getTemplate?.isSuccess &&
                getTemplate?.data?.result?.map((data) => (
                  <Option key={data._id} value={data._id}>
                    {data.name}
                  </Option>
                ))}
            </Select>
          </div>

          {classId && (
            <div className="flex items-center gap-3 pt-4">
              <Button
                type="primary"
                loading={bulkBusy}
                // Every student already has a card for this template, so
                // another run would add nothing.
                disabled={
                  !classTemplateId || (rows.length > 0 && tally.pending === 0)
                }
                onClick={handleGenerateAll}
              >
                Generate All
              </Button>
              <Button
                loading={bulkBusy}
                disabled={draftIdsInSelection.length === 0}
                onClick={handleMarkReady}
              >
                Mark Ready
                {draftIdsInSelection.length > 0
                  ? ` (${draftIdsInSelection.length})`
                  : ""}
              </Button>
              <span className="text-sm text-gray-500 pl-2">
                {tally.ready} ready &middot; {tally.draft} draft &middot;{" "}
                {tally.notified} notified &middot; {tally.pending} not generated
              </span>
            </div>
          )}

          <div className="py-5">
            <Table
              rowSelection={{
                type: "checkbox",
                ...rowSelection,
              }}
              rowKey="_id"
              columns={columns}
              dataSource={
                getStudent?.isSuccess &&
                getStudent?.data?.result.length > 0 &&
                getStudent?.data?.result
                  ? getStudent?.data?.result
                  : []
              }
              pagination={true}
              loading={getStudent?.isLoading}
              // Nine columns no longer fit a laptop width. Without this antd
              // shrinks them to fit, which collapsed the remark field to a few
              // pixels wide. Scrolling keeps every column usable instead.
              scroll={{ x: 1650 }}
            />
            {classId && (
              <div className="flex justify-center items-center py-5">
                <div className="inline-flex items-center space-x-2">
                  <Button
                    size="large"
                    type="primary"
                    htmlType="submit"
                    className="text-white"
                    onClick={handleNotify}
                    disabled={progressIds?.length === 0}
                  >
                    Notify
                    {progressIds?.length > 0 ? ` (${progressIds.length})` : ""}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <Modal
            title="Applied Template"
            visible={isModalVisible}
            onOk={() => setIsModalVisible(false)}
            onCancel={() => setIsModalVisible(false)}
          >
            <ul>
              {selectedRecord
                ?.slice()
                .reverse()
                .map((template, index) => (
                  <li
                    key={template?.template?._id || template?.template || index}
                    className="capitalize"
                  >
                    {" "}
                    {`${index + 1}. ${template?.template?.name || template?.name || template?.template || "Template"}`}
                  </li>
                ))}
            </ul>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default GenerateCard;
